import { useCallback, useEffect, useRef, useState } from 'react'
import { useScrollbarVisibility } from './useScrollbarVisibility'

interface UseAutoScrollbarReturn {
  /** Ref to attach to the scrollable container */
  containerRef: React.RefObject<HTMLDivElement | null>
  /** Whether the scrollbar should be visible */
  scrollbarVisible: boolean
  /** Whether the top fade gradient should be shown */
  showTopFade: boolean
  /** Whether the bottom fade gradient should be shown */
  showBottomFade: boolean
  /** onScroll handler to attach to the scrollable container */
  onScroll: () => void
}

/**
 * **Bildlaufleiste** plus Verlaufs-Einblendungen für einen Scroller, der
 * seinen Ref nicht selbst hält.
 *
 * Wann die Leiste zu sehen ist, entscheidet `useScrollbarVisibility` — hier
 * kommt allein dazu, dass oben und unten ein Verlauf anzeigt, dass es
 * weitergeht. Wer nur die Leiste braucht (und sein Element ohnehin schon als
 * Ref hält), nimmt den kleinen Hook direkt.
 *
 * Ein früheres `flash()` ist entfallen: Es ließ die Leiste beim Tab- und
 * Lebensbereich-Wechsel ohne Zutun aufblitzen und widersprach damit der Regel,
 * dass nur der Bildlauf sie zeigt (CONTEXT.md, „Bildlaufleiste").
 *
 * **`resetKey`:** Wer sein Element über einen `key` austauscht (die Sidebar beim Wechsel des
 * Lebensbereichs), gibt denselben Schlüssel mit — sonst hingen die Beobachter am ausgehängten
 * Element, und die Pfeile zeigten den Stand des alten Inhalts, bis man scrollt (#285).
 *
 * @example
 * const { containerRef, scrollbarVisible, showTopFade, showBottomFade, onScroll } = useAutoScrollbar()
 *
 * <div ref={containerRef} className={`my-scroll-area${scrollbarVisible ? ' auto-scrollbar--visible' : ''}`} onScroll={onScroll}>
 *   {children}
 * </div>
 * {showTopFade && <div className="auto-scrollbar__fade auto-scrollbar__fade--top" />}
 * {showBottomFade && <div className="auto-scrollbar__fade auto-scrollbar__fade--bottom" />}
 */
export function useAutoScrollbar(resetKey?: string): UseAutoScrollbarReturn {
  const containerRef = useRef<HTMLDivElement>(null)
  const [showTopFade, setShowTopFade] = useState(false)
  const [showBottomFade, setShowBottomFade] = useState(false)
  const { visible: scrollbarVisible, onScroll: onScrollVisibility } =
    useScrollbarVisibility({ ref: containerRef })
  const rafRef = useRef<number | null>(null)

  const updateFades = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    setShowTopFade(el.scrollTop > 0)
    setShowBottomFade(el.scrollTop + el.clientHeight < el.scrollHeight - 1)
  }, [])

  // Auf ein Rendern je Frame gedrosselt, wie im kleinen Hook — `scroll` feuert
  // weit häufiger. Die Sichtbarkeit drosselt sich dort selbst.
  const onScroll = useCallback(() => {
    onScrollVisibility()
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      updateFades()
    })
  }, [onScrollVisibility, updateFades])

  // Update fades on container resize AND when the scrollable content
  // changes size (e.g. a page switch that caps page height so the content
  // suddenly fits without overflow — the container itself doesn't resize in
  // that case, so we also observe its direct children).
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    // Gleich einmal messen: Der Beobachter meldet erst im nächsten Bild, bis dahin stünden die
    // Pfeile noch auf dem Stand des vorigen Elements.
    updateFades()
    const observer = new ResizeObserver(updateFades)
    observer.observe(el)
    for (const child of Array.from(el.children)) {
      observer.observe(child)
    }
    // Watch for added/removed children so dynamically mounted pages are
    // observed too.
    const mutationObserver = new MutationObserver(() => {
      updateFades()
      observer.disconnect()
      observer.observe(el)
      for (const child of Array.from(el.children)) {
        observer.observe(child)
      }
    })
    mutationObserver.observe(el, { childList: true })
    return () => {
      observer.disconnect()
      mutationObserver.disconnect()
    }
  }, [updateFades, resetKey])

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
  }, [])

  return { containerRef, scrollbarVisible, showTopFade, showBottomFade, onScroll }
}
